import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'White Labeling',
  content: `
White labeling lets you present Pliable under your own brand. You can customize the logo, favicon, page title, theme colors, set up a custom domain, and configure single sign-on (SSO) so your users never see Pliable branding or a Pliable login page.

All white labeling settings are managed from **Settings** in the Pliable app. Branding options live under the **White Label** tab, while custom domains and SSO are on the **General** tab.

---

## Custom Branding

Navigate to **Settings > White Label** to configure your brand appearance.

### Enable / Disable

Use the **Enable White Labeling** toggle to turn your custom branding on or off. Disabling it reverts to default Pliable branding, but your settings are preserved and can be re-enabled at any time.

### Logo and Favicon

Upload a custom logo and favicon to replace the default Pliable assets. These appear in the sidebar navigation and the browser tab, respectively.

- **Logo** — Recommended format: PNG or SVG with a transparent background. The logo is displayed at a max height of 64px in the sidebar.
- **Favicon** — Recommended format: PNG or ICO, 32x32 or 64x64 pixels.

### Page Title

Set a custom page title that appears in the browser tab (e.g., "Acme Analytics" instead of "Pliable").

### Theme Colors

Customize the color palette used throughout the application:

| Setting | What it controls |
|---------|-----------------|
| **Primary** | Buttons, links, active states, and primary UI accents |
| **Secondary** | Secondary UI elements and highlights |
| **Chart 1–5** | The color series used in charts and data visualizations |
| **Card Title Color** | The text color of card/section headers |
| **Card Header BG** | The background color of card headers (can be set to a custom color or turned off for a transparent/borderless look) |

Click **Save Colors** to apply your changes, or **Restore Default Colors** to reset everything back to the Pliable defaults.

---

## Custom Domains

A custom domain lets your users access Pliable at your own URL (e.g., \`analytics.yourcompany.com\`) instead of \`app.pliable.co\`.

Navigate to **Settings > General** and scroll to the **Custom Domain** card.

### Setup Steps

1. **Enter your domain** — Type the domain you want to use (e.g., \`analytics.yourcompany.com\`) and click **Save Domain**.

2. **Add DNS records** — After saving, Pliable will display two CNAME records you need to add in your DNS provider:

   - **Certificate Validation CNAME** — This proves you own the domain. AWS Certificate Manager uses it to issue an SSL certificate.
   - **Domain CNAME** — Points your custom domain to Pliable's servers (e.g., \`analytics.yourcompany.com → app.pliable.co\`).

3. **Wait for DNS propagation** — DNS changes can take anywhere from a few minutes to several hours to propagate.

4. **Verify** — Once DNS records are in place, click **Verify Certificate** in the Pliable UI. If validation succeeds, the certificate is attached and your custom domain goes live.

### Certificate Statuses

| Status | Meaning |
|--------|---------|
| **Pending Validation** | Waiting for you to add the DNS validation CNAME record |
| **Active** | Certificate verified, domain is live and serving traffic |
| **Failed** | Validation failed or expired — save the domain again to request a new certificate |

### Changing or Removing a Domain

- To **change** the domain, enter a new one and click Save. The old certificate is automatically cleaned up and a new one is requested.
- To **remove** the custom domain, clear the field and click Save. Traffic will revert to \`app.pliable.co\`.

---

## Single Sign-On (OIDC)

Pliable supports SSO via **OpenID Connect (OIDC)**, allowing your users to log in with your organization's identity provider (Okta, Azure AD, Google Workspace, Auth0, etc.).

Navigate to **Settings > General** and scroll to the **SSO (OIDC)** card.

> **Tip:** SSO works best with a custom domain. Without one, the SSO callback URL will use the main Pliable domain, which may be confusing for your users.

### Configuring SSO

1. **Create an OIDC application** in your identity provider (IdP). You'll need:
   - The **Redirect / Callback URI** shown in the Pliable SSO card
   - The **Sign-in Redirect / Login URL** shown in the card
   - The **Sign-out Redirect / Logout URL** shown in the card

2. **Enter your IdP details** in Pliable:
   - **Issuer URL** — Your IdP's OIDC issuer (e.g., \`https://your-org.okta.com\`). Pliable auto-discovers the authorization, token, and userinfo endpoints from \`.well-known/openid-configuration\`.
   - **Client ID** — The OIDC client ID from your IdP.
   - **Client Secret** — The OIDC client secret from your IdP. This is stored securely and is never exposed after saving.

3. **Set a Default Role** — Choose which Pliable role new SSO users receive on their first login:
   - **Admin** — Full access including user management and settings
   - **Builder** — Can create and modify data models, dashboards, etc.
   - **ReadOnly** — View-only access to dashboards and data
   - **ReadOnly_Partner** — Restricted view-only access (requires dimension filters to be configured before any data is visible)

4. **Auto-provision users** (optional) — When enabled, users who authenticate through your IdP are automatically created in Pliable on their first login. When disabled, users must be manually invited before they can sign in via SSO.

5. Click **Enable SSO** (or **Update SSO Configuration** if editing an existing setup).

### How SSO Login Works

When SSO is configured and a user visits your custom domain:

1. The user is redirected to your identity provider's login page.
2. After authenticating with the IdP, they are redirected back to Pliable.
3. Pliable validates the OIDC token and creates a session.
4. If auto-provisioning is on and the user doesn't exist yet, their account is created with the configured default role.

Subsequent page loads in the same browser tab skip the IdP redirect (the session is cached for the tab).

### Enabling / Disabling SSO

Use the toggle in the SSO card header to enable or disable SSO without deleting your configuration. Disabling SSO means users will log in via the standard email/password flow instead.

To fully remove SSO, click **Remove SSO** to delete the configuration entirely.

---

## Putting It All Together

For a fully white-labeled experience, we recommend configuring all three features:

1. **Custom Domain** — so users visit \`analytics.yourcompany.com\` instead of \`app.pliable.co\`
2. **SSO** — so users log in with their existing company credentials, never seeing a Pliable login page
3. **Custom Branding** — so the application matches your brand identity with your logo, colors, and page title

With all three configured, your users will have a seamless, branded analytics experience with no visible Pliable branding.
`,
};

export default doc;
